/**
 * PWA install helpers — Add to Home Screen for owners + cleaners.
 * iPhone: Add to Home Screen only works in Safari (not Chrome / Google app).
 */

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

export type InstallGuidance =
  | { kind: "standalone" }
  | { kind: "native" } // Chrome/Edge Android or desktop with beforeinstallprompt
  | { kind: "ios-safari" } // Share → Add to Home Screen
  | { kind: "ios-open-safari" } // Currently Chrome/in-app — must open Safari first
  | { kind: "manual" };

let deferredPrompt: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();

function notify() {
  listeners.forEach((l) => l());
}

export function isStandaloneDisplay(): boolean {
  if (typeof window === "undefined") return false;
  const mq = window.matchMedia("(display-mode: standalone)").matches;
  const iosStandalone = (navigator as Navigator & { standalone?: boolean }).standalone === true;
  return mq || iosStandalone;
}

export function isIosDevice(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

/** True Safari on iOS (not Chrome, Firefox, Edge, in-app browsers). */
export function isIosSafari(): boolean {
  if (!isIosDevice()) return false;
  const ua = navigator.userAgent;
  const webkit = /WebKit/.test(ua);
  const criOS = /CriOS/.test(ua);
  const fxIOS = /FxiOS/.test(ua);
  const edgiOS = /EdgiOS/.test(ua);
  // Instagram/FB/etc often include WebKit without CriOS — exclude known in-app markers
  const inApp = /FBAN|FBAV|Instagram|Line\//i.test(ua);
  return webkit && !criOS && !fxIOS && !edgiOS && !inApp;
}

export function isIosChromeOrInApp(): boolean {
  return isIosDevice() && !isIosSafari() && !isStandaloneDisplay();
}

export function canNativeInstall(): boolean {
  return !!deferredPrompt;
}

export function getInstallGuidance(): InstallGuidance {
  if (isStandaloneDisplay()) return { kind: "standalone" };
  if (canNativeInstall()) return { kind: "native" };
  if (isIosSafari()) return { kind: "ios-safari" };
  if (isIosDevice()) return { kind: "ios-open-safari" };
  return { kind: "manual" };
}

export function subscribeInstallAvailability(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

export function registerPwaInstallListeners(): void {
  if (typeof window === "undefined") return;
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredPrompt = e as BeforeInstallPromptEvent;
    notify();
  });
  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    notify();
  });
}

export async function promptNativeInstall(): Promise<"accepted" | "dismissed" | "unavailable"> {
  if (!deferredPrompt) return "unavailable";
  const promptEvent = deferredPrompt;
  deferredPrompt = null;
  notify();
  await promptEvent.prompt();
  const { outcome } = await promptEvent.userChoice;
  return outcome;
}

export async function copyAppUrl(): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(window.location.origin + "/");
    return true;
  } catch {
    return false;
  }
}

export function registerServiceWorker(): void {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) return;
  const host = window.location.hostname;
  const ok =
    window.location.protocol === "https:" ||
    host === "localhost" ||
    host === "127.0.0.1";
  if (!ok) return;
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch((err) => {
      console.warn("[pwa] SW register failed", err);
    });
  });
}
